import {
    FormEventHandler,
    ReactElement,
    useCallback,
    useEffect,
    useState
} from 'react';
import { Trans } from 'react-i18next';
import {
    UIButton,
    UIFormInput,
    UIInlineAlert,
    UISpacer,
    UIText,
    UITextButton
} from '@/ui';
import {
    useConfirmSignupVerificationAction,
    useRequestSignupVerificationAction
} from '../actions';
import { useMeTranslation } from '../hooks';
import styles from './confirm-signup-verification.module.scss';
import { useInterval } from '@/common/hooks';

type ConfirmSignupVerificationProps = {
    email: string;
    onSuccess?: (data: { token: string }) => void;
    onChangeEmail?: () => void;
};

export function ConfirmSignupVerification(
    props: ConfirmSignupVerificationProps
): ReactElement {
    const { email, onSuccess, onChangeEmail } = props;

    const { t } = useMeTranslation();

    // Two actions, so each button reads its own spinner — resending a code and
    // submitting one are separate calls and no longer share a loading flag.
    const resend = useRequestSignupVerificationAction();
    const confirm = useConfirmSignupVerificationAction();

    const error = confirm.error || resend.error;
    const clearError = () => {
        confirm.clearError();
        resend.clearError();
    };

    return (
        <>
            <UIText variant="h2" bold>
                {t('components.confirm_signup_verification.title')}
            </UIText>
            <UISpacer multiplier={2} />
            <UIText>
                <Trans
                    t={t}
                    i18nKey="components.confirm_signup_verification.description"
                    values={{ email }}
                    components={{ b: <UIText bold /> }}
                />
            </UIText>
            <UISpacer multiplier={2} />
            {error && (
                <>
                    <UIInlineAlert text={error} onClose={clearError} />
                    <UISpacer multiplier={2} />
                </>
            )}
            <ConfirmSignupVerificationForm
                loading={confirm.loading}
                resendLoading={resend.loading}
                onResend={() => {
                    resend.requestSignupVerification({ email }).catch(() => {}); // Handled in the action
                }}
                onChangeEmail={onChangeEmail}
                onSubmit={(data) => {
                    confirm
                        .confirmSignupVerification({ email, code: data.code })
                        .then((token) => {
                            onSuccess?.({ token });
                        })
                        .catch(() => {}); // Handled in the action
                }}
            />
        </>
    );
}

/** The verification code is always this many characters. */
const CODE_LENGTH = 6;

/** How long the user must wait between requesting verification codes. */
const RESEND_COOLDOWN_SECONDS = 60;

type ConfirmSignupVerificationFormProps = {
    onSubmit?: (data: { code: string }) => void;
    onResend?: () => void;
    onChangeEmail?: () => void;
    loading?: boolean;
    resendLoading?: boolean;
};
export function ConfirmSignupVerificationForm(
    props: ConfirmSignupVerificationFormProps
): ReactElement {
    const { t } = useMeTranslation();

    const { onSubmit, onResend, onChangeEmail, loading, resendLoading } = props;

    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    /**
     * Code. Held uppercase so it reads back the way it appears in the email;
     * the API matches it case insensitively either way.
     */
    const [code, setCode] = useState('');
    const [codeError, setCodeError] = useState('');
    const checkCode = useCallback(
        (code: string): string => {
            const trimmed = code.trim();
            if (!trimmed) {
                return t(
                    'components.confirm_signup_verification.code_error_required'
                );
            } else if (trimmed.length !== CODE_LENGTH) {
                return t(
                    'components.confirm_signup_verification.code_error_length'
                );
            } else if (!/^[A-Z0-9]+$/.test(trimmed)) {
                return t(
                    'components.confirm_signup_verification.code_error_invalid'
                );
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setCodeError(checkCode(code));
        }
    }, [code, showRealTimeErrors, checkCode]);

    /**
     * Resend cooldown. A code was sent to reach this step, so the countdown
     * starts straight away and restarts on every resend.
     */
    const [secondsUntilResend, setSecondsUntilResend] = useState(
        RESEND_COOLDOWN_SECONDS
    );
    useInterval(() => {
        setSecondsUntilResend((seconds) => (seconds > 0 ? seconds - 1 : 0));
    }, 1000);

    function handleResend() {
        setSecondsUntilResend(RESEND_COOLDOWN_SECONDS);
        onResend?.();
    }

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        if (!checkCode(code)) {
            setShowRealTimeErrors(false);
            onSubmit?.({ code: code.trim() });
        }
    };

    let resendText = t('components.confirm_signup_verification.resend');
    if (resendLoading) {
        resendText = t('components.confirm_signup_verification.resend_sending');
    } else if (secondsUntilResend > 0) {
        resendText = t(
            'components.confirm_signup_verification.resend_countdown',
            {
                seconds: secondsUntilResend
            }
        );
    }

    return (
        <div className={styles.container}>
            <form onSubmit={handleSubmit} noValidate>
                <UIFormInput
                    name="code"
                    label={t(
                        'components.confirm_signup_verification.code_label'
                    )}
                    value={code}
                    autoFocus={true}
                    type="text"
                    errorText={codeError}
                    required
                    maxLength={CODE_LENGTH}
                    onChange={(value) => setCode(value.toUpperCase())}
                />

                <UISpacer multiplier={2} />

                <div className={styles.buttonContainer}>
                    <UIButton
                        variant="primary"
                        type="submit"
                        loading={loading}
                        disabled={loading || resendLoading}
                    >
                        {t('components.confirm_signup_verification.confirm')}
                    </UIButton>
                    <UISpacer multiplier={1} />
                    <UITextButton
                        variant="primary"
                        type="button"
                        loading={resendLoading}
                        disabled={
                            loading || resendLoading || secondsUntilResend > 0
                        }
                        onClick={handleResend}
                    >
                        {resendText}
                    </UITextButton>
                </div>

                <UISpacer multiplier={2} />

                <UITextButton
                    variant="primary"
                    type="button"
                    disabled={loading || resendLoading}
                    onClick={() => onChangeEmail?.()}
                >
                    {t('components.confirm_signup_verification.change_email')}
                </UITextButton>
            </form>
        </div>
    );
}
